import React, { useState, useRef } from "react";
import { 
  FiMaximize2, 
  FiExternalLink, 
  FiFileText, 
  FiEdit, 
  FiTrash2,
  FiCamera,
  FiActivity,
  FiAlertCircle,
  FiEye,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import CameraStreamWebRTC from "../../../components/CameraStreamWebRTC";  // ✅ CHANGED: Use WebRTC
import PTZControls from "./PTZControls";

/**
 * LiveStreamCard
 * ========================================
 * Professional camera card with:
 * - YouTube-inspired hover overlays
 * - Smooth animations
 * - Quick actions
 * - PTZ controls (if applicable)
 * - Status indicators
 * - Click to focus/maximize
 * - Double-click to fullscreen
 * 
 * ✅ FIXED: Now uses WebRTC like camera detail page!
 * ✅ FIXED: Wrapped in React.memo to prevent re-renders!
 */
function LiveStreamCard({
  camera,
  status,
  size = "medium", // thumbnail | medium | large | list
  focused,
  onFocus,
  updateStreamStatus,
  onEditCamera,
  onDeleteCamera,
  canManageCameras,
  onViewLogs, 
  shouldPlay = false,
}) {

  console.log('🎥 LiveStreamCard render:', {
    cameraId: camera?.id,
    cameraName: camera?.name,
    shouldPlay: shouldPlay,
    focused: focused,
    size: size
  });

  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showOverlay, setShowOverlay] = useState(false);
  const [showPTZ, setShowPTZ] = useState(false);
  const clickTimer = useRef(null);

  const isPTZ = camera.camera_type === "ptz";
  const isOnline = status === "online";
  const isOffline = status === "offline";
  const isInactive = !camera.is_active;

  // Size classes
  const sizeClasses = {
    thumbnail: "h-32",
    medium: "aspect-video",
    large: "aspect-video",
    list: "h-48",
  };

  // Theme classes
  const cardBg = theme === "dark" 
    ? "bg-neutral-750 border-neutral-700" 
    : "bg-white border-gray-200";

  // Status badge
  const statusBadge = isInactive ? {
    bg: "bg-gray-500",
    text: "Inactive",
  } : isOnline ? {
    bg: "bg-green-500",
    text: "Live",
  } : {
    bg: "bg-red-500",
    text: "Offline",
  };

  // Handle single click (focus) vs double click (fullscreen)
  const handleClick = () => {
    if (clickTimer.current) {
      // Double click - go to camera detail
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      navigate(`/camera/${camera.id}`);
    } else {
      // Single click - focus
      clickTimer.current = setTimeout(() => {
        onFocus();
        clickTimer.current = null;
      }, 200);
    }
  };

  // Pop-out to new window
  const popOut = () => {
    window.open(
      `/camera/${camera.id}`,
      `camera-${camera.id}`,
      'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no'
    );
  };

  return (
    <div
      className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${cardBg} ${
        focused 
          ? "ring-4 ring-blue-500 ring-opacity-50 border-blue-500 scale-[1.02]" 
          : "border-transparent hover:border-blue-400"
      }`}
      onClick={handleClick}
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => {
        setShowOverlay(false);
        setShowPTZ(false);
      }}
    >
      {/* Video Stream */}
      <div className={`relative bg-black ${sizeClasses[size]}`}>
        {/* ✅ FIXED: Use WebRTC like camera detail page! */}
        {shouldPlay ? (
          <CameraStreamWebRTC
            path={`cam${camera.id}`}
            baseUrl="https://vision.viewmax.net"
            muted
            autoplay
            isActive={camera.is_active !== false}  // ✅ Pass active status
            cameraName={camera.name}                // ✅ For logging
            className="w-full h-full"
            onFallback={() => {
              console.warn(`WebRTC failed for camera ${camera.id}`);
              updateStreamStatus?.(camera.id, "offline");
            }}
          />
        ) : (
          /* Thumbnail/Placeholder when not streaming */
          <div className="w-full h-full flex items-center justify-center bg-gray-900">
            <FiCamera className="text-gray-600" size={48} />
          </div>
        )}

        {/* Status Badge - Top Left */}
        <div className="absolute top-2 left-2 z-10">
          <div className={`px-2 py-1 rounded-md text-xs font-semibold text-white backdrop-blur-sm ${statusBadge.bg} flex items-center gap-1.5`}>
            <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
            {statusBadge.text}
          </div>
        </div>

        {/* Logs Button - Top Right (always visible) */}
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onViewLogs) {
                onViewLogs(camera);
              }
            }}
            className="p-2 rounded-md text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all"
            title="View camera logs"
          >
            <FiFileText size={16} />
          </button>
        </div>

        {/* YouTube-Style Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-200 ${
            showOverlay ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Camera Info - Bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-white font-semibold text-sm mb-1 truncate">
              {camera.name || "Unnamed Camera"}
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-300">
              <span className="flex items-center gap-1">
                <FiCamera size={12} />
                {camera.ip_address || "—"}
              </span>
              {camera.location && (
                <span className="truncate">
                  {camera.location}
                </span>
              )}
            </div>
          </div>

          {/* Quick Actions - Top Center */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="flex items-center gap-2">
              {/* Maximize */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/camera/${camera.id}`);
                }}
                className="p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white transition-all hover:scale-110"
                title="Open camera detail"
              >
                <FiMaximize2 size={20} />
              </button>

              {/* Pop-out */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  popOut();
                }}
                className="p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white transition-all hover:scale-110"
                title="Pop-out to new window"
              >
                <FiExternalLink size={20} />
              </button>

              {/* PTZ Controls */}
              {isPTZ && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPTZ(!showPTZ);
                  }}
                  className="p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white transition-all hover:scale-110"
                  title="PTZ controls"
                >
                  <FiActivity size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Management Actions - Always Visible for Admins (Bottom Right) */}
        {canManageCameras && (
          <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditCamera(camera);
              }}
              className="p-2 rounded-lg bg-black/50 hover:bg-blue-600 backdrop-blur-md text-white transition-all"
              title="Edit camera"
            >
              <FiEdit size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete camera "${camera.name}"?`)) {
                  onDeleteCamera(camera.id);
                }
              }}
              className="p-2 rounded-lg bg-black/50 hover:bg-red-600 backdrop-blur-md text-white transition-all"
              title="Delete camera"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        )}

        {/* PTZ Controls Overlay */}
        {showPTZ && isPTZ && (
          <PTZControls
            camera={camera}
            onClose={() => setShowPTZ(false)}
          />
        )}

        {/* Offline State */}
        {(isOffline || isInactive) && !showOverlay && !shouldPlay && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="text-center">
              <FiAlertCircle className="mx-auto text-gray-400 mb-2" size={32} />
              <div className="text-white text-sm font-medium">
                {isInactive ? "Camera Inactive" : "Camera Offline"}
              </div>
              <div className="text-gray-400 text-xs mt-1">
                No stream available
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Focus Indicator */}
      {focused && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 border-4 border-blue-500 rounded-xl animate-pulse" />
        </div>
      )}
    </div>
  );
}

// ✅ CRITICAL: Prevent re-renders that destroy WebRTC connection!
export default React.memo(LiveStreamCard, (prevProps, nextProps) => {
  // Only re-render if these critical props change:
  return (
    prevProps.camera.id === nextProps.camera.id &&
    prevProps.shouldPlay === nextProps.shouldPlay &&
    prevProps.focused === nextProps.focused &&
    prevProps.status === nextProps.status &&
    prevProps.size === nextProps.size
  );
});
